import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";

interface LoginPageProps {
  onLogin: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onLogin();
    } catch (err: any) {
      setError(
        err.code === "auth/invalid-credential"
          ? "Nesprávný email nebo heslo"
          : err.code === "auth/too-many-requests"
          ? "Příliš mnoho pokusů. Zkuste to za chvíli."
          : "Přihlášení selhalo"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#050505",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        position: "relative",
      }}
    >
      {/* Grain overlay */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          opacity: 0.035,
          mixBlendMode: "overlay",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Card */}
      <div
        className="animate-fade-in-up animate-delay-1"
        style={{
          width: "100%",
          maxWidth: "420px",
          borderRadius: "18px",
          padding: "3rem 2.5rem",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          boxShadow: "0 32px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <h1
            className="text-gradient"
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: "2rem",
              letterSpacing: "-0.04em",
              margin: "0 0 0.5rem",
            }}
          >
            SENÁT
          </h1>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
              fontSize: "0.8125rem",
              color: "#666666",
              margin: 0,
              letterSpacing: "0.04em",
            }}
          >
            monitoring politických kampaní
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div>
            <label
              style={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 500,
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase" as const,
                color: "#a1a1a1",
                display: "block",
                marginBottom: "0.375rem",
              }}
            >
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="dwx-input"
              placeholder="vas@email.cz"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label
              style={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 500,
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase" as const,
                color: "#a1a1a1",
                display: "block",
                marginBottom: "0.375rem",
              }}
            >
              Heslo
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="dwx-input"
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div
              role="alert"
              style={{
                borderRadius: "8px",
                padding: "0.625rem 0.875rem",
                background: "rgba(248, 113, 113, 0.08)",
                border: "1px solid rgba(248, 113, 113, 0.25)",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 300,
                fontSize: "0.8125rem",
                color: "#f87171",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary-hero"
            style={{
              width: "100%",
              justifyContent: "center",
              marginTop: "0.5rem",
              opacity: loading ? 0.6 : 1,
              fontSize: "0.9375rem",
              padding: "0.75rem 1.25rem",
            }}
          >
            {loading ? (
              <>
                <span
                  className="dwx-spinner"
                  style={{ width: "1rem", height: "1rem", borderWidth: "2px" }}
                />
                Přihlašuji...
              </>
            ) : (
              "Přihlásit se"
            )}
          </button>
        </form>

        <p
          style={{
            textAlign: "center",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300,
            fontSize: "0.6875rem",
            color: "#444444",
            marginTop: "1.75rem",
            letterSpacing: "0.03em",
          }}
        >
          Přístup pouze pro autorizované uživatele
        </p>
      </div>
    </div>
  );
};
